import type { StatusVariant } from '../components/ui/StatusBadge';
import type {
  AssignedCountSheet, CountLineComparison, CountLineStatus, CountSalesRefreshResult, CountSheetStatus, ManagerCountLine, ManagerCountSheet,
} from '../types/countSheets';
import { formatWasteQuantity } from './wasteReview';

// F4 · lógica PURA de «Conteos» (sin renderizador, con pruebas). Quien cuenta nunca ve el sistema; el
// gerente ve lo contado, lo que decía el sistema a esa hora y lo que se aplicaría al aprobar.

export const COUNT_LINE_LABELS: Record<CountLineStatus, string> = {
  PENDING: 'Por contar',
  COUNTED: 'Contado, falta enviar',
  SUBMITTED: 'Por aprobar',
  APPROVED: 'Aprobado',
  REJECTED: 'Rechazado',
  RECOUNT: 'Se pidió recontar',
  NOT_COUNTED: 'No se contó',
  CANCELLED: 'Cancelado',
};

export const COUNT_SHEET_LABELS: Record<CountSheetStatus, string> = {
  OPEN: 'Por contar',
  SUBMITTED: 'Por revisar',
  CLOSED: 'Terminada',
  CANCELLED: 'Cancelada',
};

export function lineBadgeVariant(status: CountLineStatus): StatusVariant {
  if (status === 'APPROVED') return 'success';
  if (status === 'SUBMITTED') return 'warning';
  if (status === 'REJECTED') return 'danger';
  if (status === 'COUNTED' || status === 'RECOUNT') return 'info';
  return 'neutral';
}

export function sheetBadgeVariant(status: CountSheetStatus): StatusVariant {
  if (status === 'SUBMITTED') return 'warning';
  if (status === 'OPEN') return 'info';
  if (status === 'CLOSED') return 'success';
  return 'neutral';
}

const money = (value: string | number) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(value));

/** «2 por contar · 3 por aprobar · 1 aprobado»: el avance de una lista en una línea. */
export function sheetProgressText(counts: Partial<Record<CountLineStatus, number>>): string {
  const parts: string[] = [];
  const add = (status: CountLineStatus, one: string, many: string) => {
    const count = counts[status] ?? 0;
    if (count) parts.push(`${count} ${count === 1 ? one : many}`);
  };
  add('PENDING', 'por contar', 'por contar');
  add('COUNTED', 'contado sin enviar', 'contados sin enviar');
  add('SUBMITTED', 'por aprobar', 'por aprobar');
  add('APPROVED', 'aprobado', 'aprobados');
  add('RECOUNT', 'a recontar', 'a recontar');
  add('REJECTED', 'rechazado', 'rechazados');
  add('NOT_COUNTED', 'no contado', 'no contados');
  add('CANCELLED', 'cancelado', 'cancelados');
  return parts.join(' · ') || 'Sin insumos';
}

/** «1 producto» / «3 productos»: sin «(s)» en la pantalla. */
export function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** Los enviados que «Aprobar todo» sí aprobaría (los de diferencia grande se aprueban aparte, con motivo). */
export function approvableInBulk(sheet: Pick<ManagerCountSheet, 'lines'>): number {
  return sheet.lines.filter((line) => line.status === 'SUBMITTED' && !approvalComparison(line)?.large).length;
}

export type BulkAckKind = 'sales_sync_ack_missing' | 'supply_ack_missing';

/**
 * «Aprobar todo» con avisos por confirmar: se pregunta UNO a la vez (primero Loyverse, luego compras
 * pendientes), diciendo a qué insumos aplica, y solo se confirma ese aviso. Así nunca se acepta un aviso
 * que nadie vio. null = no queda nada por confirmar.
 */
export function nextBulkAck(results: Array<{ ingredientName: string; status: string; message: string | null }>): { kind: BulkAckKind; ingredients: string[]; message: string } | null {
  for (const kind of ['sales_sync_ack_missing', 'supply_ack_missing'] as const) {
    const affected = results.filter((result) => result.status === kind);
    if (!affected.length) continue;
    const ingredients = [...new Set(affected.map((result) => result.ingredientName))];
    const message = kind === 'sales_sync_ack_missing'
      // Este aviso solo llega después de revisar las ventas solas: si sigue, es que Loyverse no se pudo revisar.
      ? 'No se pudo revisar Loyverse desde la hora del conteo: puede haber ventas de antes que todavía no llegan y se descontarían dos veces.'
      : `Hay compras o transferencias pendientes de ${ingredients.join(', ')} que todavía no cuentan en el inventario. Si ese producto ya llegó (o ya salió), apruébalas o confírmalas antes; si no, aprueba de todos modos.`;
    return { kind, ingredients, message };
  }
  return null;
}

/**
 * ¿Hace falta «Actualizar ventas» en la tarjeta de esta lista? Solo para lo que aprobar no resuelve solo:
 * insumos que no se dejaron contar porque había ventas de Loyverse de antes sin descontar (el servidor lo
 * marca con la misma revisión que usa aprobar). Lo enviado no lo necesita: al aprobar se revisan las
 * ventas solas. Nunca en una tienda desactivada. null = no hace falta y el botón no sale.
 */
export function salesRefreshNeed(sheet: Pick<ManagerCountSheet, 'lines' | 'store'>): string | null {
  if (!sheet.store.isActive) return null;
  return sheet.lines.some((line) => (line.status === 'PENDING' || line.status === 'COUNTED') && line.pendingSales)
    ? 'Hay insumos que no se dejaron contar por ventas de Loyverse sin descontar. Actualiza y pide que los intenten otra vez.'
    : null;
}

/**
 * Tras revisar las ventas solas al aprobar: ¿lo que se aplicaría sigue igual a lo que el gerente vio? Solo
 * entonces se aprueba sin volver a preguntar; si cambió (llegó un ticket de antes) o ya no se puede, se muestra.
 */
export function sameApproval(seen: Pick<ManagerCountLine, 'atApproval' | 'comparison'>, fresh: Pick<ManagerCountLine, 'status' | 'atApproval' | 'comparison'> | null): boolean {
  if (!fresh || fresh.status !== 'SUBMITTED' || fresh.atApproval?.wouldGoNegative || fresh.atApproval?.problem) return false;
  const before = approvalComparison(seen);
  const after = approvalComparison(fresh);
  return Boolean(before && after && before.difference === after.difference && before.large === after.large);
}

/** Lo que hizo «Actualizar ventas», en palabras. `problem` = algo no se pudo y se muestra como aviso. */
export function salesRefreshText(result: CountSalesRefreshResult): { text: string; problem: boolean } {
  const parts: string[] = [];
  let problem = false;
  const { sync, posting } = result;
  // Con el descuento automático encendido, el propio sync ya descontó (todo el negocio).
  const autoPosted = sync.status === 'synced' ? sync.autoPosted ?? 0 : 0;
  if (sync.status === 'synced') {
    const created = sync.receiptsCreated;
    parts.push(!created ? 'Loyverse ya estaba al día: no había tickets nuevos.' : created === 1 ? 'Llegó 1 ticket nuevo de Loyverse.' : `Llegaron ${created} tickets nuevos de Loyverse.`);
    if (autoPosted) parts.push(autoPosted === 1 ? 'El descuento automático descontó 1 venta.' : `El descuento automático descontó ${autoPosted} ventas.`);
  } else if (sync.status === 'not_connected') {
    parts.push('Este negocio no tiene Loyverse conectado.');
  } else {
    parts.push(sync.message);
    problem = true;
  }
  if (posting.status === 'posted') {
    const posted = posting.linesPosted;
    if (posted) parts.push(posted === 1 ? 'Se descontó 1 venta.' : `Se descontaron ${posted} ventas.`);
    if (posting.linesSkipped) {
      parts.push(`${plural(posting.linesSkipped, 'venta no se pudo', 'ventas no se pudieron')} descontar${posting.skippedProducts.length ? ` (${posting.skippedProducts.join(', ')})` : ''}: resuélvelas en Inventario → Descontar ventas.`);
      problem = true;
    }
    if (!posted && !posting.linesSkipped && !autoPosted) parts.push('No había ventas por descontar.');
  } else if (posting.status === 'nothing_pending') {
    if (!autoPosted) parts.push('No había ventas por descontar.');
  } else {
    parts.push(posting.message);
    problem = true;
  }
  return { text: parts.join(' '), problem };
}

/** «faltan 7 pz (≈ $35.00 · 2.2 % de lo que decía)», «sobran…» o «cuadra». */
export function differenceText(comparison: Pick<CountLineComparison, 'difference' | 'direction' | 'value' | 'percentOfSystem'>, unitSymbol: string): string {
  if (comparison.direction === 'MATCH') return 'cuadra';
  const magnitude = formatWasteQuantity(String(Math.abs(Number(comparison.difference))), unitSymbol);
  const extras = [
    comparison.value !== null ? `≈ ${money(comparison.value)}` : 'sin costo registrado',
    comparison.percentOfSystem !== null ? `${comparison.percentOfSystem.toLocaleString('es-MX', { maximumFractionDigits: 1 })} % de lo que decía` : null,
  ].filter(Boolean).join(' · ');
  return `${comparison.direction === 'SHORTAGE' ? 'faltan' : 'sobran'} ${magnitude} (${extras})`;
}

/** «Contó 318 pz · el sistema decía 325 pz → faltan 7 pz (≈ $35.00)». null si todavía no se contó. */
export function countedText(line: Pick<ManagerCountLine, 'countedQuantity' | 'expectedQuantity' | 'comparison' | 'unit'>): string | null {
  if (line.countedQuantity === null || line.expectedQuantity === null || !line.comparison) return null;
  const symbol = line.unit.symbol;
  return `Contó ${formatWasteQuantity(line.countedQuantity, symbol)} · el sistema decía ${formatWasteQuantity(line.expectedQuantity, symbol)} → ${differenceText(line.comparison, symbol)}`;
}

/**
 * Si después del conteo llegó algo con hora ANTERIOR (un ticket de Loyverse atrasado), lo que debía haber
 * a esa hora cambia: esto es lo que de verdad se aplicaría al aprobar.
 */
export function atApprovalText(line: Pick<ManagerCountLine, 'atApproval' | 'unit'>): string | null {
  const at = line.atApproval;
  if (!at) return null;
  if (at.problem === 'parallel_balance') return 'No se puede aprobar: el insumo tiene existencia en otra unidad en esta tienda.';
  if (at.wouldGoNegative && at.resultingQuantity !== undefined) return `No se puede aprobar: la existencia quedaría en ${formatWasteQuantity(at.resultingQuantity, line.unit.symbol)} (después del conteo salió más de lo que se contó). Revisa el conteo o pide recontar.`;
  if (!at.changedSinceCount || at.expectedQuantity === undefined || !at.direction || at.difference === undefined) return null;
  const detail = at.direction === 'MATCH' ? 'ya cuadra' : differenceText({ difference: at.difference, direction: at.direction, value: at.value ?? null, percentOfSystem: at.percentOfSystem ?? null }, line.unit.symbol);
  return `Después llegaron movimientos con hora anterior al conteo: a esa hora debía haber ${formatWasteQuantity(at.expectedQuantity, line.unit.symbol)} → ${detail}.`;
}

/** Lo que se aplicaría al aprobar (lo recalcula el servidor al aprobar; esto es para mostrar). */
export function approvalComparison(line: Pick<ManagerCountLine, 'atApproval' | 'comparison'>): CountLineComparison | null {
  const at = line.atApproval;
  if (at && !at.problem && at.direction && at.difference !== undefined) {
    return { difference: at.difference, direction: at.direction, value: at.value ?? null, percentOfSystem: at.percentOfSystem ?? null, large: at.large ?? false };
  }
  return line.comparison;
}

/** «Se aplicó: faltan 7 pz» / «Cuadró: no se movió nada». */
export function appliedText(line: Pick<ManagerCountLine, 'applied' | 'unit'>): string | null {
  if (!line.applied) return null;
  const difference = Number(line.applied.difference);
  if (!difference) return 'Cuadró: no se movió el inventario.';
  const magnitude = formatWasteQuantity(String(Math.abs(difference)), line.unit.symbol);
  const waste = line.applied.detectedWasteQuantity ? ` (${formatWasteQuantity(line.applied.detectedWasteQuantity, line.unit.symbol)} como merma detectada en conteo)` : '';
  return difference < 0 ? `Se aplicó: faltaban ${magnitude}${waste}.` : `Se aplicó: sobraban ${magnitude}.`;
}

/** Primero las listas con algo por aprobar, luego las que se están contando; dentro, las más recientes. */
export function sortSheetsForManager(sheets: ManagerCountSheet[]): ManagerCountSheet[] {
  const rank = (sheet: ManagerCountSheet) => ((sheet.counts.SUBMITTED ?? 0) > 0 ? 0 : sheet.status === 'OPEN' ? 1 : 2);
  return [...sheets].sort((a, b) => rank(a) - rank(b) || b.createdAt.localeCompare(a.createdAt));
}

/** «Elegir todos»: suma a lo ya elegido los insumos que se ven (con búsqueda, solo los que coinciden). */
export function withAllVisible(selected: ReadonlySet<string>, visibleIds: string[]): Set<string> {
  return new Set([...selected, ...visibleIds]);
}

/** «Elegir todos (42)» sin búsqueda; con búsqueda, «Elegir los 3 que se ven». */
export function pickAllLabel(visibleCount: number, searching: boolean): string {
  if (!searching) return `Elegir todos (${visibleCount})`;
  return visibleCount === 1 ? 'Elegir el que se ve' : `Elegir los ${visibleCount} que se ven`;
}

/** «Repetir»: la misma tienda, la misma persona y los mismos insumos (sin repetir los recuentos). */
export function repeatDraft(sheet: Pick<ManagerCountSheet, 'store' | 'assignedTo' | 'note' | 'lines'>) {
  const ingredientIds = [...new Set(sheet.lines.map((line) => line.ingredient.id))];
  return { storeId: sheet.store.id, assignedToUserId: sheet.assignedTo?.id ?? '', ingredientIds, note: sheet.note ?? '' };
}

/** ¿Se puede enviar? Hace falta al menos un insumo contado; los que falten se mandan como «no contado». */
export function submitState(sheet: Pick<AssignedCountSheet, 'status' | 'lines'>): { canSubmit: boolean; reason: string | null; uncounted: string[] } {
  if (sheet.status !== 'OPEN') return { canSubmit: false, reason: 'Esta lista ya se envió.', uncounted: [] };
  const counted = sheet.lines.filter((line) => line.status === 'COUNTED').length;
  const uncounted = sheet.lines.filter((line) => line.status === 'PENDING').map((line) => line.ingredient.name);
  if (!counted) return { canSubmit: false, reason: 'Cuenta al menos un producto para poder enviar.', uncounted };
  return { canSubmit: true, reason: null, uncounted };
}

/** Lo que falta por hacer en las listas abiertas de quien cuenta. */
export function assignedProgress(sheet: Pick<AssignedCountSheet, 'lines'>): { toCount: number; counted: number; blocked: number } {
  return {
    toCount: sheet.lines.filter((line) => line.status === 'PENDING').length,
    counted: sheet.lines.filter((line) => line.status === 'COUNTED').length,
    blocked: sheet.lines.filter((line) => line.status === 'PENDING' && line.blocked).length,
  };
}
